import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { LogOut, Hourglass, RefreshCw } from "lucide-react";

export default function PendingApproval() {
  const { profile, signOut, signingOut, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const intervalRef = useRef(null);

  // Auto-poll every 15s so the screen unlocks the moment an admin approves.
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      refreshProfile();
      setTick((t) => t + 1);
    }, 15000);
    return () => clearInterval(intervalRef.current);
  }, [refreshProfile]);

  const onLogout = async () => {
    console.log("[auth] logout (pending) clicked from UI");
    try { await signOut(); } catch (err) { console.error("[auth] signOut threw", err); }
    console.log("[auth] navigate /login (hard reload)");
    window.location.replace("/login");
  };

  const checkNow = async () => {
    setBusy(true);
    try { await refreshProfile(); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#05050A] px-4">
      <div className="w-full max-w-md text-center" data-testid="pending-approval-screen">
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/10 flex items-center justify-center">
            <Hourglass size={20} strokeWidth={1.5} className="text-[#F59E0B] animate-pulse" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#F59E0B]">Pending approval</div>
            <h1 className="text-2xl font-semibold tracking-tight mt-1">Awaiting admin approval</h1>
          </div>
        </div>

        <p className="text-sm text-[#A0A0AB] leading-relaxed">
          An administrator must approve your account before platform access is enabled.
          You will gain full access automatically as soon as approval is granted —
          there is nothing else you need to do right now.
        </p>

        {profile?.email ? (
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0F0F14] border border-border text-xs">
            <span className="text-[#71717A] uppercase tracking-widest">Signed in as</span>
            <span className="font-mono">{profile.email}</span>
          </div>
        ) : null}

        <div className="mt-6 text-[10px] uppercase tracking-widest text-[#71717A]">
          Auto-checking every 15s · last check #{tick}
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            data-testid="pending-recheck-btn"
            onClick={checkNow}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-[#00E676]/30 bg-[#00E676]/10 hover:bg-[#00E676]/20 text-[#00E676] text-sm disabled:opacity-50"
          >
            <RefreshCw size={14} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
            {busy ? "Checking…" : "Check now"}
          </button>
          <button
            data-testid="pending-logout-btn"
            onClick={onLogout}
            disabled={signingOut}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-border bg-[#1A1A22] hover:bg-[#262633] text-sm disabled:opacity-50 disabled:cursor-wait"
          >
            {signingOut ? (
              <>
                <span className="inline-block w-3 h-3 border-2 border-[#71717A] border-t-white rounded-full animate-spin" />
                Signing out…
              </>
            ) : (
              <>
                <LogOut size={14} strokeWidth={1.5} /> Sign out
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
